/**
 * Script-type vocabulary translation.
 *
 * The grouping file stores **lowercase, sorted** script types — the Middleware
 * reads that file at run time and uses them for queue-tier routing, so the
 * casing and ordering are load-bearing, not cosmetic.
 *
 * The app has always used capitalised step-type tokens (`Api`, `Web-UI`,
 * `Mobile`, `Database`); those drive the `.automation-step-tag--*` badge styles
 * and the Automation Scripts filters. Translating in one place keeps the file
 * correct without renaming every badge in the app.
 *
 * @module features/executionStudio/utils/scriptTypes
 */

/** App token → file value. */
const TO_FILE = {
  'Api': 'api',
  'Web-UI': 'web',
  'Mobile': 'mobile',
  'Database': 'database',
};

/** File value → app token, for badges on the way back. */
const FROM_FILE = {
  api: 'Api',
  web: 'Web-UI',
  'web-ui': 'Web-UI',
  mobile: 'Mobile',
  'mobile-ui': 'Mobile',
  database: 'Database',
  db: 'Database',
};

/**
 * Capitalised app tokens → the lowercase, sorted list the file must hold.
 * Sorted so the same membership always serialises identically.
 */
export const toFileScriptTypes = (types = []) =>
  [...new Set(
    (types || [])
      .filter(Boolean)
      .map((t) => TO_FILE[t] || String(t).toLowerCase())
  )].sort();

/** File values → the app tokens the badges are styled for. */
export const fromFileScriptTypes = (types = []) =>
  [...new Set(
    (types || [])
      .filter(Boolean)
      .map((t) => FROM_FILE[String(t).toLowerCase()] || t)
  )];

/**
 * Metadata step types → the badge-able app tokens only. The metadata file lists
 * every step type a script uses (e.g. `ReuseFromScript`), which are not script
 * types and must not reach the grouping file.
 */
export const knownScriptTypes = (types = []) =>
  fromFileScriptTypes(types).filter((t) => Object.prototype.hasOwnProperty.call(TO_FILE, t));
